import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, CalendarClock, ChevronLeft, ChevronRight, Plus, Check,
  Trash2, CalendarDays, Clock, AlertTriangle, User, X, StickyNote, Euro,
  Sparkles, Loader2, Lock, GripVertical, ChevronUp, ChevronDown, Zap, MoreHorizontal
} from 'lucide-react';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import EntryDetail from '../components/EntryDetail';
import EmptyState from '../components/EmptyState';
import DateField from '../components/DateField';
import SearchableClientSelect from '../components/SearchableClientSelect';
import InlineDatePicker from '../components/InlineDatePicker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CASE_DESCRIPTION_LABELS, COURT_TYPE_LABELS, DOCUMENT_TYPE_LABELS, capitalizeFirst } from '../components/EntriesList';
import PostponementWizard from '../components/PostponementWizard';
import MyrtoChat from '../components/MyrtoChat';

const HEARING_STATUS_OPTIONS = [
  { value: 'ΣΕ ΑΝΑΜΟΝΗ', label: 'Σε αναμονή' },
  { value: 'ΣΥΖΗΤΗΘΗΚΕ', label: 'Συζητήθηκε' },
  { value: 'ΑΝΑΒΟΛΗ', label: 'Αναβολή' },
  { value: 'ΜΑΤΑΙΩΣΗ', label: 'Ματαίωση' },
  { value: 'ΠΑΡΑΙΤΗΣΗ ΑΠΟ ΔΙΚΟΓΡΑΦΟ', label: 'Παραίτηση' },
];
const HEARING_STATUS_LABELS = Object.fromEntries(HEARING_STATUS_OPTIONS.map((o) => [o.value, o.label]));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAY_START = 7; // 07:00
const DAY_END = 21; // 21:00
const SLOT_PX = 60; // ύψος ανά ημίωρο
const HOUR_PX = SLOT_PX * 2;

const toLocalYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatGreekDate = (ymd) => {
  const d = new Date(`${ymd}T12:00:00`);
  return d.toLocaleDateString('el-GR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
};

const minutesFromDayStart = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h - DAY_START) * 60 + m;
};

const upperNoAccents = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();

const clientLabel = (client) => {
  if (!client) return '';
  const label = client.client_type === 'legal' && client.company_name
    ? client.company_name
    : [client.surname, client.name].filter(Boolean).join(' ');
  return label.toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

const normGr = (s) => (s || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const stripClientFromTitle = (title, clientName) => {
  if (!title || !clientName) return title;
  const nTitle = normGr(title);
  const candidates = [clientName.trim(), clientName.trim().split(/\s+/).reverse().join(' ')];
  for (const c of candidates) {
    const nc = normGr(c);
    if (!nc) continue;
    const idx = nTitle.indexOf(nc);
    if (idx !== -1) {
      let out = (title.slice(0, idx) + title.slice(idx + nc.length))
        .replace(/^\s*[—–\-·:,|(]+\s*/, '')
        .replace(/\s*[—–\-·:,|(]+\s*$/, '')
        .replace(/\(\s*\)/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
      return out || title;
    }
  }
  return title;
};

const DURATIONS = [
  { value: 30, label: '30 λεπτά' }, { value: 60, label: '1 ώρα' }, { value: 90, label: '1,5 ώρα' },
  { value: 120, label: '2 ώρες' }, { value: 150, label: '2,5 ώρες' }, { value: 180, label: '3 ώρες' },
  { value: 210, label: '3,5 ώρες' }, { value: 240, label: '4 ώρες' },
];

export default function DayPlanner({ embedded = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  const [selectedDate, setSelectedDate] = useState(
    dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) ? dateParam : toLocalYMD(new Date())
  );
  const [tasks, setTasks] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [swipe, setSwipe] = useState({ id: null, dx: 0, x: 0, y: 0 });
  const swipedRef = useRef(false);
  const [newTask, setNewTask] = useState({ title: '', start_time: '', duration_minutes: 30, notes: '', client_id: '' });
  const [newClientMode, setNewClientMode] = useState(false);
  const [newClient, setNewClient] = useState({ surname: '', name: '' });
  const [hearingStatusOpen, setHearingStatusOpen] = useState(null);
  const [hearingUpdating, setHearingUpdating] = useState(null);
  const [postponeEntry, setPostponeEntry] = useState(null);
  const [actionsOpenId, setActionsOpenId] = useState(null);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [rescheduleEntryItem, setRescheduleEntryItem] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [dayNotes, setDayNotes] = useState([]);
  const [editingNote, setEditingNote] = useState(null); // { id: string|null, text }
  const [quickAdd, setQuickAdd] = useState(null); // { time: 'HH:MM' }
  const [quickTitle, setQuickTitle] = useState('');
  const [cases, setCases] = useState([]);
  const [detailEntry, setDetailEntry] = useState(null);
  const [billingTask, setBillingTask] = useState(null);
  const [activeTimer, setActiveTimer] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cp_billing_timer')) || null; } catch (e) { return null; }
  });
  const [timerNow, setTimerNow] = useState(Date.now());
  useEffect(() => {
    if (!activeTimer) return undefined;
    const iv = setInterval(() => setTimerNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [activeTimer]);
  const timerElapsedSec = activeTimer ? Math.max(0, Math.floor((timerNow - activeTimer.start) / 1000)) : 0;
  const aiModalRef = useRef(null);
  const autoScrollOnDrag = (e) => {
    const el = aiModalRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const margin = 70;
    if (e.clientY < rect.top + margin) el.scrollTop -= 16;
    else if (e.clientY > rect.bottom - margin) el.scrollTop += 16;
  };
  const [billingForm, setBillingForm] = useState({ client_id: '', minutes: 30, rate: '', description: '', mode: 'hourly', flat_amount: '' });
  const [doneAsk, setDoneAsk] = useState(null);
  const timeRecordedIds = useRef(new Set());
  const [office, setOffice] = useState(null);
  const [viewMember, setViewMember] = useState('');
  const viewMemberRef = useRef('');
  const viewingOtherMember = Boolean(office && viewMember && viewMember !== office.my_id);
  const [aiModal, setAiModal] = useState(null); // null | 'setup' | 'loading' | 'priority' | 'distributing' | 'preview'
  const [aiMode, setAiMode] = useState('full'); // 'full' | 'redistribute'
  const [aiDays, setAiDays] = useState([]);
  const [aiHours, setAiHours] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('cp_ai_hours'));
      if (saved?.start && saved?.end) return saved;
    } catch (e) { /* αγνόησε */ }
    return { start: '09:00', end: '17:00' };
  });
  const updateAiHours = (next) => {
    setAiHours(next);
    try { localStorage.setItem('cp_ai_hours', JSON.stringify(next)); } catch (e) { /* αγνόησε */ }
  };
  const [aiProposal, setAiProposal] = useState([]);
  const [aiPriority, setAiPriority] = useState([]);
  const [aiDragFrom, setAiDragFrom] = useState(null);
  const [aiDaysInfo, setAiDaysInfo] = useState([]);
  const [aiSaving, setAiSaving] = useState(false);
  const [refillOpen, setRefillOpen] = useState(false);
  const [refillTasks, setRefillTasks] = useState([]);
  const [refillSaving, setRefillSaving] = useState(false);
  const [viewMode, setViewMode] = useState('day'); // 'day' | 'week'
  const [weekTasks, setWeekTasks] = useState([]);

  const todayYMD = toLocalYMD(new Date());
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const fn = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  const readOnly = (embedded && isDesktop) || viewingOtherMember;
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const fn = (e) => setIsNarrow(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);

  const loadPlan = useCallback(async (date) => {
    try {
      const params = { date };
      if (viewMemberRef.current) params.member_id = viewMemberRef.current;
      const { data } = await axios.get(`${API}/day-plan`, { params });
      setTasks(data.tasks || []);
      setOverdue(data.overdue || []);
      setDayNotes(data.notes || []);
      setEditingNote(null);
    } catch (e) {
      toast.error('Αδυναμία φόρτωσης προγράμματος');
    }
  }, []);

  const saveEditingNote = async () => {
    if (!editingNote) return;
    const text = editingNote.text.trim();
    if (!text) { setEditingNote(null); return; }
    try {
      if (editingNote.id) {
        await axios.put(`${API}/day-notes/${editingNote.id}`, { text });
      } else {
        await axios.post(`${API}/day-notes`, { date: selectedDate, text });
      }
      loadPlan(selectedDate);
    } catch (e) {
      toast.error('Σφάλμα αποθήκευσης σημείωσης');
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/day-notes/${noteId}`);
      loadPlan(selectedDate);
    } catch (e) {
      toast.error('Σφάλμα διαγραφής σημείωσης');
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const [entriesRes, clientsRes, casesRes] = await Promise.all([
          axios.get(`${API}/entries`),
          axios.get(`${API}/clients`),
          axios.get(`${API}/cases`),
        ]);
        setEntries(entriesRes.data || []);
        setClients(clientsRes.data || []);
        setCases(casesRes.data || []);
      } catch (e) {
        toast.error('Αδυναμία φόρτωσης εγγραφών');
      }
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    if (embedded) return;
    axios.get(`${API}/office`).then(({ data }) => {
      setOffice(data);
      setViewMember(data.my_id);
      viewMemberRef.current = data.my_id;
    }).catch(() => {});
  }, [embedded]);

  const switchMember = async (memberId) => {
    setViewMember(memberId);
    viewMemberRef.current = memberId;
    loadPlan(selectedDate);
    try {
      const { data } = await axios.get(`${API}/entries`, { params: { member_id: memberId } });
      setEntries(data || []);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { loadPlan(selectedDate); }, [selectedDate, loadPlan]);

  const clientMap = useMemo(() => {
    const m = {};
    clients.forEach((c) => { m[c.id] = clientLabel(c); });
    return m;
  }, [clients]);

  // Ταξινόμηση εγγραφών της ημέρας (τοπική ώρα)
  const dayEntries = useMemo(() => {
    return entries.filter((e) => {
      if (!e.date) return false;
      const d = new Date(e.date);
      if (isNaN(d)) return false;
      return toLocalYMD(d) === selectedDate;
    });
  }, [entries, selectedDate]);

  const classified = useMemo(() => {
    const appointments = [];
    const hearings = [];
    const deadlines = [];
    const works = [];
    const otherScheduled = [];
    dayEntries.forEach((e) => {
      const title = (e.title || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const d = new Date(e.date);
      const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
      const _time = hasTime ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : null;
      if (e.entry_type === 'other_activity' && title.includes('ΡΑΝΤΕΒΟΥ')) {
        appointments.push({ ...e, _time, _duration: e.duration_minutes || 60 });
      } else if (e.entry_type === 'hearing' || title.includes('ΔΙΚΑΣΙΜΟΣ')) {
        const rawT = (e.date || '').slice(11, 16);
        const hearingTime = /^\d{2}:\d{2}$/.test(rawT) && rawT !== '00:00' ? rawT : null;
        hearings.push({ ...e, _time: hearingTime, _isHearing: true });
      } else if (e.entry_type === 'deadline' || e.entry_type === 'court_action' || (e.entry_type === 'other_activity' && title.includes('ΠΡΟΘΕΣΜΙΑ ΕΡΓΑΣΙΑΣ'))) {
        deadlines.push(e);
      } else if (e.entry_type === 'other_activity' && title.includes('ΕΡΓΑΣΙΑ')) {
        works.push({ ...e, _time, _duration: e.duration_minutes || 30 });
      } else {
        otherScheduled.push({ ...e, _time });
      }
    });
    return { appointments, hearings, deadlines, works, otherScheduled };
  }, [dayEntries]);

  const timedTasks = tasks.filter((t) => t.start_time);
  const untimedTasks = tasks.filter((t) => !t.start_time);

  // ---- Εβδομαδιαία προβολή ----
  const weekDays = useMemo(() => {
    const d = new Date(`${selectedDate}T12:00:00`);
    const monday = new Date(d);
    monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => {
      const dd = new Date(monday);
      dd.setDate(monday.getDate() + i);
      return toLocalYMD(dd);
    });
  }, [selectedDate]);

  useEffect(() => {
    if (viewMode !== 'week' || embedded) return;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/day-tasks/range`, { params: { start: weekDays[0], end: weekDays[6] } });
        setWeekTasks(data || []);
      } catch (e) { /* σιωπηλά */ }
    };
    load();
  }, [viewMode, weekDays, embedded]);

  const weekData = useMemo(() => {
    if (viewMode !== 'week') return [];
    return weekDays.map((date) => {
      const items = [];
      entries.forEach((e) => {
        if (!e.date) return;
        const d = new Date(e.date);
        if (isNaN(d) || toLocalYMD(d) !== date) return;
        const title = (e.title || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const rawT = (e.date || '').slice(11, 16);
        const t = /^\d{2}:\d{2}$/.test(rawT) && rawT !== '00:00' ? rawT : null;
        if (e.entry_type === 'hearing' || title.includes('ΔΙΚΑΣΙΜΟΣ')) {
          items.push({ key: `h-${e.id}`, cls: 'bg-amber-50 border-amber-300 text-amber-900', time: t, label: 'Δικάσιμος', sub: clientMap[e.client_id] });
        } else if (e.entry_type === 'other_activity' && title.includes('ΡΑΝΤΕΒΟΥ')) {
          items.push({ key: `a-${e.id}`, cls: 'bg-amber-50 border-amber-300 text-slate-800', time: t, label: clientMap[e.client_id] || 'Ραντεβού', sub: clientMap[e.client_id] ? 'Ραντεβού' : null });
        } else if (e.entry_type === 'deadline' || e.entry_type === 'court_action') {
          items.push({ key: `d-${e.id}`, cls: 'bg-white border-red-200 text-red-800', time: null, label: capitalizeFirst(e.title), sub: clientMap[e.client_id] });
        }
      });
      weekTasks.filter((tk) => tk.date === date).forEach((tk) => {
        const cName = clientMap[tk.client_id] || '';
        const cleanTitle = capitalizeFirst(stripClientFromTitle(tk.title, cName));
        items.push({
          key: `t-${tk.id}`,
          cls: tk.status === 'done' ? 'bg-stone-50 border-stone-200 text-slate-400 line-through' : 'bg-amber-50 border-amber-300 text-slate-800',
          time: tk.start_time || null,
          label: cName || cleanTitle,
          sub: cName ? cleanTitle : null,
        });
      });
      items.sort((a, b) => (a.time || '99:99').localeCompare(b.time || '99:99'));
      return { date, items };
    });
  }, [viewMode, weekDays, entries, weekTasks, clientMap]);

  const timedAgenda = useMemo(() => {
    const rows = [
      ...classified.hearings.map((item) => ({ item: { ...item, _time: item._time || '09:00' }, kind: 'hearing' })),
      ...classified.appointments.filter((a) => a._time).map((item) => ({ item, kind: 'appointment' })),
      ...classified.works.filter((w) => w._time).map((item) => ({ item, kind: 'work' })),
      ...timedTasks.map((item) => ({ item, kind: 'task' })),
    ];
    return rows.sort((a, b) =>
      (a.item._time || a.item.start_time).localeCompare(b.item._time || b.item.start_time));
  }, [classified, timedTasks]);

  const shiftDay = (delta) => {
    const d = new Date(`${selectedDate}T12:00:00`);
    d.setDate(d.getDate() + delta);
    setSelectedDate(toLocalYMD(d));
  };

  const addTask = async () => {
    if (!newTask.title.trim()) { toast.error('Γράψτε τίτλο εργασίας'); return; }
    try {
      let clientId = newTask.client_id || '';
      if (newClientMode && newClient.surname.trim()) {
        const res = await axios.post(`${API}/clients`, {
          client_type: 'natural',
          surname: upperNoAccents(newClient.surname.trim()),
          name: upperNoAccents(newClient.name.trim()),
        });
        clientId = res.data.id;
        setClients((prev) => [...prev, res.data]);
      }
      await axios.post(`${API}/day-tasks`, {
        date: selectedDate,
        title: newTask.title.trim(),
        start_time: newTask.start_time || null,
        duration_minutes: parseInt(newTask.duration_minutes, 10) || 30,
        notes: newTask.notes || '',
        client_id: clientId,
      });
      setNewTask({ title: '', start_time: '', duration_minutes: 30, notes: '', client_id: '' });
      setNewClientMode(false);
      setNewClient({ surname: '', name: '' });
      setShowAdd(false);
      loadPlan(selectedDate);
      toast.success('Η εργασία προστέθηκε');
    } catch (e) {
      toast.error('Αδυναμία προσθήκης');
    }
  };

  const toggleDone = async (task) => {
    const newStatus = task.status === 'done' ? 'pending' : 'done';
    try {
      await axios.put(`${API}/day-tasks/${task.id}`, { status: newStatus });
      loadPlan(selectedDate);
      if (newStatus === 'done' && !timeRecordedIds.current.has(task.id)) {
        const planned = task.duration_minutes || 30;
        const nearest = DURATIONS.reduce((best, d) => (Math.abs(d.value - planned) < Math.abs(best - planned) ? d.value : best), 30);
        setDoneAsk({ task, minutes: nearest });
      }
      if (newStatus === 'done' && !embedded && task.client_id) {
        const cl = clients.find((c) => c.id === task.client_id);
        if (cl?.email) {
          toast('Να ειδοποιηθεί ο πελάτης;', {
            description: `Αποστολή email στον/στην ${clientLabel(cl)} ότι η εργασία ολοκληρώθηκε`,
            duration: 10000,
            action: {
              label: 'Αποστολή email',
              onClick: async () => {
                try {
                  await axios.post(`${API}/notifications/task-complete/${task.id}`);
                  toast.success('Το email στάλθηκε στον πελάτη');
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Αποτυχία αποστολής email');
                }
              },
            },
          });
        }
      }
      if (newStatus === 'done' && !embedded && selectedDate >= todayYMD) {
        if (!tasks.some((t) => t.id !== task.id && t.status === 'pending')) {
          offerRefill();
        } else if ((task.duration_minutes || 30) >= 60) {
          offerRedistribute('done');
        }
      }
    } catch (e) { toast.error('Σφάλμα ενημέρωσης'); }
  };

  const offerRefill = async () => {
    try {
      const { data } = await axios.get(`${API}/day-tasks/upcoming`, { params: { date: selectedDate } });
      if (!data.length) return;
      toast('Ολοκληρώσατε όλες τις εργασίες της ημέρας', {
        description: 'Θέλετε να φέρετε μπροστά εργασίες επόμενων ημερών;',
        duration: 10000,
        action: {
          label: 'Ξαναγέμισμα',
          onClick: () => {
            setRefillTasks(data.map((t) => ({ ...t, _checked: false })));
            setRefillOpen(true);
          },
        },
      });
    } catch (e) { /* σιωπηλά */ }
  };

  const acceptRefill = async () => {
    const chosen = refillTasks.filter((t) => t._checked);
    if (!chosen.length) { toast.error('Επιλέξτε εργασίες'); return; }
    setRefillSaving(true);
    let ok = 0;
    for (const t of chosen) {
      try {
        await axios.put(`${API}/day-tasks/${t.id}`, { date: selectedDate });
        ok++;
      } catch (e) { /* συνέχεια */ }
    }
    setRefillSaving(false);
    setRefillOpen(false);
    loadPlan(selectedDate);
    toast.success(`Μεταφέρθηκαν ${ok} εργασίες στην ημέρα`);
  };

  const deleteTask = async (task) => {
    try {
      await axios.delete(`${API}/day-tasks/${task.id}`);
      loadPlan(selectedDate);
      toast.success('Διαγράφηκε');
    } catch (e) { toast.error('Σφάλμα διαγραφής'); }
  };

  const rescheduleTask = async (taskId, date) => {
    if (!date) { toast.error('Επιλέξτε ημερομηνία'); return; }
    try {
      await axios.put(`${API}/day-tasks/${taskId}`, { date });
      setRescheduleId(null);
      setRescheduleDate('');
      loadPlan(selectedDate);
      toast.success(`Μεταφέρθηκε στις ${new Date(`${date}T12:00:00`).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`);
    } catch (e) { toast.error('Σφάλμα μεταφοράς'); }
  };

  const moveOverdueHere = async (task) => rescheduleTask(task.id, selectedDate);

  const toggleEntryDone = async (entry) => {
    const newStatus = entry.status === 'ΟΛΟΚΛΗΡΩΜΕΝΗ' ? 'ΣΕ ΑΝΑΜΟΝΗ' : 'ΟΛΟΚΛΗΡΩΜΕΝΗ';
    try {
      await axios.patch(`${API}/entries/${entry.id}/status`, { status: newStatus });
      refreshEntries();
    } catch (e) { toast.error('Σφάλμα ενημέρωσης'); }
  };

  const deleteEntry = async (entry) => {
    try {
      await axios.delete(`${API}/entries/${entry.id}`);
      refreshEntries();
      toast.success('Διαγράφηκε');
    } catch (e) { toast.error('Σφάλμα διαγραφής'); }
  };

  const rescheduleEntry = async (entry, date) => {
    if (!date) { toast.error('Επιλέξτε ημερομηνία'); return; }
    const time = (entry.date || '').slice(11, 16);
    const newDate = /^\d{2}:\d{2}$/.test(time) && time !== '00:00' ? `${date}T${time}:00` : `${date}T00:00:00`;
    try {
      await axios.patch(`${API}/entries/${entry.id}`, { date: newDate });
      setRescheduleId(null);
      setRescheduleEntryItem(null);
      setRescheduleDate('');
      refreshEntries();
      toast.success(`Μεταφέρθηκε στις ${new Date(`${date}T12:00:00`).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`);
    } catch (e) { toast.error('Σφάλμα μεταφοράς'); }
  };

  // ---- AI Οργάνωση εβδομάδας ----
  const aiDayLabel = (ymd) =>
    new Date(`${ymd}T12:00:00`).toLocaleDateString('el-GR', { weekday: 'long', day: 'numeric', month: 'short' });

  const fmtDur = (m) => {
    if (m < 60) return m === 30 ? '30 λεπτά' : `${m}΄`;
    const h = Math.floor(m / 60);
    const r = m % 60;
    const hs = `${h} ${h === 1 ? 'ώρα' : 'ώρες'}`;
    return r ? `${hs} ${r}΄` : hs;
  };

  const openAIModal = () => {
    const base = selectedDate > todayYMD ? selectedDate : todayYMD;
    const dd = new Date(`${base}T12:00:00`);
    const days = [];
    while (days.length < 10) {
      const dow = dd.getDay();
      if (dow !== 0 && dow !== 6) days.push({ date: toLocalYMD(dd), checked: true });
      dd.setDate(dd.getDate() + 1);
    }
    setAiDays(days);
    setAiProposal([]);
    setAiDaysInfo([]);
    setAiMode('full');
    setAiModal('setup');
  };

  const openRedistribute = () => {
    openAIModal();
    setAiMode('redistribute');
  };

  const offerRedistribute = (why) => {
    toast(why === 'duration' ? 'Η διάρκεια της εργασίας άλλαξε' : 'Ολοκληρώσατε εργασία νωρίτερα;', {
      description: 'Θέλετε το AI να ανακατανείμει τις εκκρεμείς εργασίες των επόμενων ημερών στον διαθέσιμο χρόνο;',
      duration: 10000,
      action: { label: 'Ανακατανομή', onClick: openRedistribute },
    });
  };

  const runRedistribute = async () => {
    const days = aiDays.filter((d) => d.checked).map((d) => d.date);
    if (!days.length) { toast.error('Επιλέξτε τουλάχιστον μία ημέρα'); return; }
    if (aiHours.start >= aiHours.end) { toast.error('Μη έγκυρες ώρες γραφείου'); return; }
    setAiModal('distributing');
    try {
      const { data: rangeTasks } = await axios.get(`${API}/day-tasks/range`, {
        params: { start: days[0], end: days[days.length - 1] },
      });
      const pending = (rangeTasks || [])
        .filter((t) => t.status === 'pending' && !t.start_time)
        .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
        .map((t) => ({
          title: t.title, duration_minutes: t.duration_minutes || 30,
          source_id: t.id, source_kind: 'task', due: null, reason: t.notes || '',
        }));
      if (!pending.length) {
        toast.info('Δεν υπάρχουν εκκρεμείς εργασίες προς ανακατανομή στις ημέρες αυτές');
        setAiModal('setup');
        return;
      }
      const { data: job } = await axios.post(`${API}/day-plan/ai-distribute`, {
        days, work_start: aiHours.start, work_end: aiHours.end, tasks: pending,
      });
      const data = await pollAiJob(job.job_id);
      const items = (data.proposal || []).map((p, i) => ({ ...p, _idx: i, _checked: true }));
      setAiDaysInfo(data.days_info || []);
      if (!items.length) {
        toast.info('Δεν χώρεσε καμία εργασία — δοκιμάστε άλλες ημέρες/ώρες');
        setAiModal('setup');
        return;
      }
      setAiProposal(items);
      setAiModal('preview');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Σφάλμα ανακατανομής');
      setAiModal('setup');
    }
  };

  const pollAiJob = async (jobId, maxMs = 300000) => {
    const t0 = Date.now();
    while (Date.now() - t0 < maxMs) {
      await new Promise((r) => setTimeout(r, 2500));
      const { data } = await axios.get(`${API}/day-plan/ai-jobs/${jobId}`);
      if (data.status === 'done') return data.result || {};
      if (data.status === 'error') throw new Error(data.error || 'Αποτυχία AI επεξεργασίας');
    }
    throw new Error('Η επεξεργασία καθυστερεί υπερβολικά — δοκιμάστε ξανά σε λίγο');
  };

  const runAI = async () => {
    const days = aiDays.filter((d) => d.checked).map((d) => d.date);
    if (!days.length) { toast.error('Επιλέξτε τουλάχιστον μία ημέρα'); return; }
    if (aiHours.start >= aiHours.end) { toast.error('Μη έγκυρες ώρες γραφείου'); return; }
    setAiModal('loading');
    try {
      const { data: job } = await axios.post(`${API}/day-plan/ai-prioritize`, {
        days, work_start: aiHours.start, work_end: aiHours.end,
      });
      const data = await pollAiJob(job.job_id);
      const items = (data.tasks || []).map((p, i) => ({ ...p, _idx: i }));
      setAiDaysInfo(data.days_info || []);
      if (!items.length) {
        toast.info(data.pool_count === 0
          ? 'Δεν βρέθηκαν εκκρεμότητες για προγραμματισμό'
          : 'Το AI δεν πρότεινε εργασίες — δοκιμάστε ξανά');
        setAiModal('setup');
        return;
      }
      setAiPriority(items);
      setAiModal('priority');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Σφάλμα AI οργάνωσης');
      setAiModal('setup');
    }
  };

  // Γρήγορη οργάνωση: 1 κλικ — σειρά & διάρκειες του AI, κατευθείαν στο τελικό πλάνο
  const runQuickAI = async () => {
    const days = aiDays.filter((d) => d.checked).map((d) => d.date);
    if (!days.length) { toast.error('Επιλέξτε τουλάχιστον μία ημέρα'); return; }
    if (aiHours.start >= aiHours.end) { toast.error('Μη έγκυρες ώρες γραφείου'); return; }
    setAiModal('loading');
    try {
      const { data: pj } = await axios.post(`${API}/day-plan/ai-prioritize`, {
        days, work_start: aiHours.start, work_end: aiHours.end,
      });
      const pdata = await pollAiJob(pj.job_id);
      const tasks = pdata.tasks || [];
      if (!tasks.length) {
        toast.info(pdata.pool_count === 0
          ? 'Δεν βρέθηκαν εκκρεμότητες για προγραμματισμό'
          : 'Το AI δεν πρότεινε εργασίες — δοκιμάστε ξανά');
        setAiModal('setup');
        return;
      }
      setAiPriority(tasks.map((p, i) => ({ ...p, _idx: i })));
      setAiModal('distributing');
      const { data: dj } = await axios.post(`${API}/day-plan/ai-distribute`, {
        days, work_start: aiHours.start, work_end: aiHours.end,
        tasks: tasks.map((t) => ({
          title: t.title, duration_minutes: t.duration_minutes,
          source_id: t.source_id || '', source_kind: t.source_kind || '',
          due: t.due || null, reason: t.reason || '', case_info: t.case_info || '',
          case_id: t.case_id || '', client_id: t.client_id || '',
        })),
      });
      const ddata = await pollAiJob(dj.job_id);
      const items = (ddata.proposal || []).map((p, i) => ({ ...p, _idx: i, _checked: true }));
      setAiDaysInfo(ddata.days_info || []);
      if (!items.length) {
        toast.info('Δεν χώρεσε καμία εργασία — δοκιμάστε άλλες ημέρες/ώρες');
        setAiModal('setup');
        return;
      }
      setAiProposal(items);
      setAiModal('preview');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Σφάλμα γρήγορης οργάνωσης');
      setAiModal('setup');
    }
  };

  const movePriority = (from, to) => {
    if (to < 0 || to >= aiPriority.length || from === to) return;
    const next = [...aiPriority];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setAiPriority(next);
  };

  const runDistribute = async () => {
    if (!aiPriority.length) { toast.error('Η λίστα είναι κενή'); return; }
    const days = aiDays.filter((d) => d.checked).map((d) => d.date);
    setAiModal('distributing');
    try {
      const { data: job } = await axios.post(`${API}/day-plan/ai-distribute`, {
        days, work_start: aiHours.start, work_end: aiHours.end,
        tasks: aiPriority.map((t) => ({
          title: t.title, duration_minutes: t.duration_minutes,
          source_id: t.source_id || '', source_kind: t.source_kind || '',
          due: t.due || null, reason: t.reason || '', case_info: t.case_info || '',
          case_id: t.case_id || '', client_id: t.client_id || '',
        })),
      });
      const data = await pollAiJob(job.job_id);
      const items = (data.proposal || []).map((p, i) => ({ ...p, _idx: i, _checked: true }));
      setAiDaysInfo(data.days_info || []);
      if (!items.length) {
        toast.info('Δεν χώρεσε καμία εργασία — δοκιμάστε άλλες ημέρες/ώρες');
        setAiModal('priority');
        return;
      }
      setAiProposal(items);
      setAiModal('preview');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Σφάλμα κατανομής');
      setAiModal('priority');
    }
  };

  const acceptAI = async () => {
    const chosen = aiProposal.filter((p) => p._checked);
    if (!chosen.length) { toast.error('Δεν επιλέξατε εργασίες'); return; }
    setAiSaving(true);
    let ok = 0;
    for (const p of chosen) {
      try {
        if (p.source_kind === 'task' && p.source_id) {
          await axios.put(`${API}/day-tasks/${p.source_id}`, {
            date: p.date, duration_minutes: p.duration_minutes, source: 'ai_planner',
          });
        } else {
          await axios.post(`${API}/day-tasks`, {
            date: p.date, title: p.title,
            duration_minutes: p.duration_minutes,
            notes: [p.case_info, p.reason].filter(Boolean).join(' — '), source: 'ai_planner',
            case_id: p.case_id || '', client_id: p.client_id || '',
          });
        }
        ok++;
      } catch (e) { /* συνέχεια με τα επόμενα */ }
    }
    setAiSaving(false);
    setAiModal(null);
    loadPlan(selectedDate);
    if (ok < chosen.length) {
      toast.warning(`Προστέθηκαν ${ok} από ${chosen.length} εργασίες — κάποιες απέτυχαν`);
    } else {
      toast.success(`Προστέθηκαν ${ok} εργασίες στο πρόγραμμα`);
    }
  };

  // Γρήγορη προσθήκη με κλικ πάνω στο ωρολόγιο
  const handleTimelineClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const totalMin = Math.floor((y / HOUR_PX) * 60);
    const snapped = Math.max(0, Math.min(totalMin - (totalMin % 30), (DAY_END - DAY_START) * 60 - 30));
    const h = DAY_START + Math.floor(snapped / 60);
    const m = snapped % 60;
    setQuickAdd({ time: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}` });
    setQuickTitle('');
  };

  const saveQuickTask = async () => {
    const title = quickTitle.trim();
    const time = quickAdd?.time;
    if (!title || !time) { setQuickAdd(null); setQuickTitle(''); return; }
    try {
      await axios.post(`${API}/day-tasks`, {
        date: selectedDate,
        title,
        start_time: time,
        duration_minutes: 30,
      });
      setQuickAdd(null);
      setQuickTitle('');
      loadPlan(selectedDate);
      toast.success(`Προστέθηκε στις ${time}`);
    } catch (e) {
      toast.error('Αδυναμία προσθήκης');
    }
  };

  // Now indicator
  const now = new Date();
  const showNowLine = selectedDate === todayYMD && now.getHours() >= DAY_START && now.getHours() < DAY_END;
  const nowTop = ((now.getHours() - DAY_START) * 60 + now.getMinutes()) / 60 * HOUR_PX;

  const timelineHeight = (DAY_END - DAY_START) * HOUR_PX;

  const scheduledItems = useMemo(() => {
    const key = (x) => (x._time || '99:99');
    // Ό,τι εμφανίζεται στο «Πρόγραμμα ημέρας» (ραντεβού με ώρα) δεν επαναλαμβάνεται εδώ
    return [...classified.appointments.filter((a) => !a._time), ...classified.otherScheduled]
      .sort((a, b) => key(a).localeCompare(key(b)));
  }, [classified]);

  const openEntry = (entry) => setDetailEntry(entry);

  const refreshEntries = async () => {
    try {
      const { data } = await axios.get(`${API}/entries`);
      setEntries(data || []);
    } catch (e) { /* noop */ }
    loadPlan(selectedDate);
  };

  const changeHearingStatus = async (entry, status) => {
    setHearingStatusOpen(null);
    if (status === 'ΑΝΑΒΟΛΗ') { setPostponeEntry(entry); return; }
    setHearingUpdating(entry.id);
    try {
      await axios.patch(`${API}/entries/${entry.id}/status`, { status });
      toast.success('Η κατάσταση ενημερώθηκε');
      refreshEntries();
    } catch (e) {
      toast.error('Σφάλμα κατά την ενημέρωση κατάστασης');
    } finally {
      setHearingUpdating(null);
    }
  };

  const openBilling = (task) => {
    setBillingTask(task);
    setBillingForm({
      client_id: task.client_id || '',
      minutes: task.duration_minutes || 30,
      rate: '',
      description: task.title || '',
      mode: 'hourly',
      flat_amount: '',
    });
  };

  const startBillingTimer = (task) => {
    const t = { taskId: task.id, start: Date.now(), title: task.title || '' };
    try { localStorage.setItem('cp_billing_timer', JSON.stringify(t)); } catch (e) { /* noop */ }
    setActiveTimer(t);
    toast.success('Το χρονόμετρο ξεκίνησε — μπορείτε να κλείσετε το παράθυρο, συνεχίζει να μετρά');
  };

  const stopBillingTimer = () => {
    const mins = Math.max(1, Math.ceil(timerElapsedSec / 60));
    try { localStorage.removeItem('cp_billing_timer'); } catch (e) { /* noop */ }
    if (activeTimer) {
      timeRecordedIds.current.add(activeTimer.taskId);
      axios.post(`${API}/task-time/record`, { title: activeTimer.title, actual_minutes: mins, source: 'timer' }).catch(() => {});
    }
    setActiveTimer(null);
    setBillingForm((f) => ({ ...f, minutes: mins, mode: 'hourly' }));
    toast.success(`Καταγράφηκαν ${mins}΄ εργασίας — έτοιμα για χρέωση`);
  };

  const fmtTimer = (s) => `${String(Math.floor(s / 3600)).padStart(2, '0')}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const submitBilling = async () => {
    const isFlat = billingForm.mode === 'flat';
    const rate = parseFloat(billingForm.rate) || 0;
    const minutes = parseInt(billingForm.minutes, 10) || 0;
    const flatAmount = parseFloat(billingForm.flat_amount) || 0;
    if (isFlat && !flatAmount) { toast.error('Συμπληρώστε το ποσό της αμοιβής'); return; }
    if (!isFlat && (!rate || !minutes)) { toast.error('Συμπληρώστε διάρκεια και ωριαία χρέωση'); return; }
    const hours = minutes / 60;
    try {
      await axios.post(`${API}/financial/entries`, {
        entry_type: 'fee',
        amount: isFlat ? Math.round(flatAmount * 100) / 100 : Math.round(hours * rate * 100) / 100,
        description: billingForm.description || billingTask.title,
        date: selectedDate,
        client_id: billingForm.client_id || null,
        case_id: billingTask.case_id || null,
        fee_type: isFlat ? 'flat' : 'hourly',
        hours: isFlat ? null : Math.round(hours * 100) / 100,
        hourly_rate: isFlat ? null : rate,
        is_paid: false,
        notes: `Από Πρόγραμμα Ημέρας — ${billingTask.title}`,
      });
      if (billingTask._kind === 'entry') {
        await axios.patch(`${API}/entries/${billingTask.id}`, { billed: true });
        refreshEntries();
      } else {
        await axios.put(`${API}/day-tasks/${billingTask.id}`, { billed: true });
      }
      toast.success('Η χρέωση καταχωρήθηκε στα Οικονομικά');
      setBillingTask(null);
      loadPlan(selectedDate);
    } catch (e) {
      toast.error('Σφάλμα καταχώρησης χρέωσης');
    }
  };

  const caseSummary = (e) => {
    const caseObj = cases.find((c) => c.id === e.case_id);
    const courtType = caseObj?.court_type || e.court_type;
    const courtLocation = caseObj?.court_location || e.court_location;
    const documentType = caseObj?.document_type || e.document_type;
    const caseDesc = e.case_description_type === 'other' && e.case_description_custom
      ? e.case_description_custom
      : e.case_description_type ? (CASE_DESCRIPTION_LABELS[e.case_description_type] || e.case_description_type) : null;
    const parts = [
      courtType ? COURT_TYPE_LABELS[courtType] || courtType : null,
      courtLocation || null,
      documentType ? DOCUMENT_TYPE_LABELS[documentType] || documentType : null,
      caseObj?.client_role || null,
      caseDesc,
    ].filter(Boolean);
    return parts.length ? parts.join(' | ') : null;
  };

  const entryCard = (e, testPrefix) => {
    const summary = e.entry_type !== 'other_activity' ? caseSummary(e) : null;
    return (
    <div
      key={e.id}
      data-testid={`${testPrefix}-${e.id}`}
      onClick={() => openEntry(e)}
      className={`bg-white border border-stone-200 rounded-lg px-3 py-2 hover:bg-stone-50 cursor-pointer transition-colors shadow-sm`}
    >
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-bold text-sm text-slate-900 truncate">
          {(clientMap[e.client_id] || 'Χωρίς πελάτη').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')}
        </h4>
        {e._time && <span className="text-xs font-semibold text-slate-500 shrink-0">{e._time}</span>}
      </div>
      {summary && (
        <p className="text-xs text-slate-600 mt-0.5" data-testid={`${testPrefix}-summary-${e.id}`} style={{ lineHeight: 1.3 }}>{summary}</p>
      )}
      {e.opponent_details && (
        <p className="text-xs text-slate-600 mt-0.5" style={{ lineHeight: 1.3 }}>Αντίδικος: {e.opponent_details}</p>
      )}
      <p className="text-sm font-bold text-red-700 mt-0.5" style={{ lineHeight: 1.3 }}>
        {capitalizeFirst(e.title)}{e.case_description_custom && (e.title || '').toUpperCase().includes('ΡΑΝΤΕΒΟΥ') ? ` — ${e.case_description_custom}` : ''}
      </p>
      {e.notes && !e._isHearing && (
        <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-wrap break-words" style={{ lineHeight: 1.3 }}>{e.notes}</p>
      )}
      {!summary && e.court && <p className="text-xs text-slate-500 truncate mt-0.5">{e.court}</p>}
    </div>
    );
  };

  const renderBlock = (item, kind) => {
    const time = item._time || item.start_time;
    const duration = kind === 'task' ? (item.duration_minutes || 30) : (item._duration || 60);
    const isDone = kind === 'task' ? item.status === 'done' : item.status === 'ΟΛΟΚΛΗΡΩΜΕΝΗ';
    const isTimeTracking = kind === 'task' && item.source === 'time_tracking';
    const doneCls = 'bg-stone-50 border-stone-200 text-slate-400 opacity-60';
    const styles = {
      appointment: isDone ? doneCls : 'bg-amber-50 border-amber-300 text-slate-800',
      work: isDone ? doneCls : 'bg-amber-50 border-amber-300 text-slate-800',
      hearing: isDone ? doneCls : 'bg-amber-50 border-amber-300 text-amber-900',
      task: isTimeTracking
        ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
        : isDone ? doneCls : 'bg-amber-50 border-amber-300 text-slate-800',
    };
    const showActions = !readOnly;
    const clickable = kind !== 'task' || !!item.case_id;
    return (
      <div
        key={`${kind}-${item.id}`}
        data-testid={`day-block-${kind}-${item.id}`}
        onClick={(e) => {
          e.stopPropagation();
          if (kind !== 'task') { openEntry(item); return; }
          if (item.case_id) {
            const rel = entries.find((en) => en.case_id === item.case_id);
            if (rel) openEntry(rel);
          }
        }}
        className={`border rounded-lg px-2.5 py-2 shadow-sm ${styles[kind]}${clickable ? ' cursor-pointer' : ''}`}
      >
        <div className="flex items-stretch gap-2">
          <div className="min-w-0 flex-1">
            {kind === 'hearing' ? (
              <p className={`text-sm font-semibold ${isDone && !isTimeTracking ? 'line-through' : ''}`} style={{ lineHeight: 1.3 }}>
                {time} · {capitalizeFirst(item.title) || 'Δικάσιμος'}
              </p>
            ) : (
              <>
                <p className={`text-sm font-semibold ${isDone && !isTimeTracking ? 'line-through' : ''}`} style={{ lineHeight: 1.3 }}>
                  {time}{kind === 'appointment' ? ` (${fmtDur(duration)})` : ''}
                  {(item.client_id && clientMap[item.client_id]) ? ` · ${clientMap[item.client_id]}` : ''}
                </p>
                <p className={`text-sm mt-0.5 ${isDone && !isTimeTracking ? 'line-through' : ''}`} style={{ lineHeight: 1.3 }}>
                  {kind === 'appointment' ? 'Ραντεβού' : capitalizeFirst(stripClientFromTitle(item.title, item.client_id ? clientMap[item.client_id] : ''))}
                  {kind === 'appointment' && item.case_description_custom ? ` — ${item.case_description_custom}` : ''}
                </p>
              </>
            )}
            {kind === 'hearing' && (item.client_id && clientMap[item.client_id]) && (
              <p className="text-xs truncate opacity-80 flex items-center gap-1 mt-0.5" style={{ lineHeight: 1.3 }}>
                <User className="w-3.5 h-3.5 shrink-0" />{clientMap[item.client_id]}
              </p>
            )}
            {kind === 'hearing' && caseSummary(item) && (
              <p className="text-xs opacity-90 mt-0.5" data-testid={`hearing-summary-${item.id}`} style={{ lineHeight: 1.3 }}>{caseSummary(item)}</p>
            )}
            {kind === 'hearing' && item.opponent_details && (
              <p className="text-xs opacity-90 mt-0.5" style={{ lineHeight: 1.3 }}>Αντίδικος: {item.opponent_details}</p>
            )}
            {kind === 'hearing' && !caseSummary(item) && item.court && (
              <p className="text-xs opacity-90 mt-0.5" style={{ lineHeight: 1.3 }}>{item.court}</p>
            )}
            {kind === 'task' && item.notes && (
              <p className="text-xs opacity-80 mt-0.5 break-words" style={{ lineHeight: 1.3 }}>{item.notes}</p>
            )}
          </div>
          <div className={`flex flex-col items-end shrink-0 gap-1 ${kind === 'hearing' ? 'justify-between' : 'justify-end'}`}>
            {kind === 'hearing' && (
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button
                  data-testid={`hearing-status-btn-${item.id}`}
                  onClick={() => setHearingStatusOpen(hearingStatusOpen === item.id ? null : item.id)}
                  disabled={hearingUpdating === item.id}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-[#D97757] text-white hover:bg-[#C15F3C] shadow-sm transition-all"
                >
                  {hearingUpdating === item.id ? (
                    <span className="animate-pulse">...</span>
                  ) : (
                    <>
                      <span>{HEARING_STATUS_LABELS[item.status] || 'Σε αναμονή'}</span>
                      <ChevronDown className="w-3 h-3" />
                    </>
                  )}
                </button>
                {hearingStatusOpen === item.id && (
                  <div className="absolute right-0 mt-1 w-48 bg-white border border-stone-200 rounded-md shadow-lg z-50">
                    {HEARING_STATUS_OPTIONS.map((o) => (
                      <button
                        key={o.value}
                        data-testid={`hearing-status-option-${o.value}`}
                        onClick={() => changeHearingStatus(item, o.value)}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-stone-100 first:rounded-t-md last:rounded-b-md ${(item.status || 'ΣΕ ΑΝΑΜΟΝΗ') === o.value ? 'bg-slate-200 font-medium text-slate-900' : 'text-slate-700'}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          {showActions && (
            <div className="flex items-center gap-1 shrink-0 justify-end" onClick={(e) => e.stopPropagation()}>
              <button data-testid={`card-actions-toggle-${item.id}`} title="Ενέργειες"
                onClick={() => setActionsOpenId(actionsOpenId === `b-${item.id}` ? null : `b-${item.id}`)}
                className="sm:hidden w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:text-slate-700">
                <MoreHorizontal className="w-4 h-4" />
              </button>
              <div className={`${actionsOpenId === `b-${item.id}` ? 'flex' : 'hidden'} sm:flex items-center gap-1`}>
              {!isTimeTracking && (
                <>
                  <button data-testid={`task-done-btn-${item.id}`} onClick={() => (kind === 'task' ? toggleDone(item) : toggleEntryDone(item))} title={isDone ? 'Αναίρεση' : 'Ολοκληρώθηκε'}
                    className={`cp-pop-on-hover w-7 h-7 rounded flex items-center justify-center border ${isDone ? 'bg-green-600 border-green-600 text-white' : 'bg-white border-stone-300 text-slate-500 hover:text-green-600'}`}>
                    <Check className="w-4 h-4" />
                  </button>
                  <button data-testid={`task-bill-btn-${item.id}`} onClick={() => openBilling(kind !== 'task' ? { ...item, duration_minutes: item._duration || 60, _kind: 'entry' } : item)} title={activeTimer?.taskId === item.id ? 'Χρονόμετρο σε εξέλιξη' : (item.billed ? 'Έχει χρεωθεί — νέα χρέωση' : 'Χρέωση')}
                    className={`w-7 h-7 rounded flex items-center justify-center border ${item.billed ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-stone-300 text-slate-500 hover:text-emerald-600'}${activeTimer?.taskId === item.id ? ' ring-2 ring-emerald-400 animate-pulse text-emerald-600' : ''}`}>
                    <Euro className="w-4 h-4" />
                  </button>
                  <button data-testid={`task-reschedule-btn-${item.id}`} onClick={() => { setRescheduleEntryItem(kind !== 'task' ? item : null); setRescheduleId(item.id); setRescheduleDate(''); }} title="Μεταφορά σε άλλη ημέρα"
                    className="w-7 h-7 rounded flex items-center justify-center border bg-white border-stone-300 text-slate-500 hover:text-red-700">
                    <CalendarDays className="w-4 h-4" />
                  </button>
                </>
              )}
              <button data-testid={`task-delete-btn-${item.id}`} onClick={() => (kind === 'task' ? deleteTask(item) : deleteEntry(item))} title="Διαγραφή"
                className="w-7 h-7 rounded flex items-center justify-center border bg-white border-stone-300 text-slate-500 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    );
  };

  const untimedTaskRow = (t) => (
    <div key={t.id} data-testid={`untimed-task-${t.id}`}
      onClick={() => {
        if (swipedRef.current) { swipedRef.current = false; return; }
        if (t.case_id) {
          const rel = entries.find((en) => en.case_id === t.case_id);
          if (rel) openEntry(rel);
        }
      }}
      onTouchStart={(e) => {
        if (readOnly) return;
        setSwipe({ id: t.id, dx: 0, x: e.touches[0].clientX, y: e.touches[0].clientY });
      }}
      onTouchMove={(e) => {
        if (readOnly || swipe.id !== t.id) return;
        const dy = Math.abs(e.touches[0].clientY - swipe.y);
        if (dy > 45) return;
        const dx = Math.max(-110, Math.min(110, e.touches[0].clientX - swipe.x));
        setSwipe((s) => ({ ...s, dx }));
      }}
      onTouchEnd={() => {
        if (readOnly || swipe.id !== t.id) return;
        const { dx } = swipe;
        setSwipe({ id: null, dx: 0, x: 0, y: 0 });
        if (Math.abs(dx) > 10) swipedRef.current = true;
        if (dx > 70 && t.status !== 'done') toggleDone(t);
        else if (dx < -70) {
          const d = new Date(`${selectedDate}T12:00:00`);
          d.setDate(d.getDate() + 1);
          rescheduleTask(t.id, d.toISOString().slice(0, 10));
        }
      }}
      style={swipe.id === t.id && swipe.dx !== 0 ? {
        transform: `translateX(${swipe.dx}px)`,
        backgroundColor: swipe.dx > 40 ? '#dcfce7' : swipe.dx < -40 ? '#ffedd5' : undefined,
        borderColor: swipe.dx > 40 ? '#86efac' : swipe.dx < -40 ? '#fdba74' : undefined,
      } : undefined}
      className={`cp-fade-in flex items-end gap-2 border rounded-lg px-2.5 py-1.5 ${t.status === 'done' ? 'bg-stone-50 border-stone-200' : 'bg-amber-50/70 border-amber-200'}${t.case_id ? ' cursor-pointer' : ''}`}>
      <div className="min-w-0 flex-1">
        {(t.client_id && clientMap[t.client_id]) && (
          <p className={`text-sm font-semibold truncate ${t.status === 'done' ? 'text-slate-400 line-through' : 'text-slate-800'}`} style={{ lineHeight: 1.3 }}>{clientMap[t.client_id]}</p>
        )}
        <p className={`text-sm ${isDesktop ? 'truncate' : 'line-clamp-3'} ${t.status === 'done' ? 'text-slate-400 line-through' : 'text-slate-800'}`} style={{ lineHeight: 1.3 }}>{capitalizeFirst(stripClientFromTitle(t.title, t.client_id ? clientMap[t.client_id] : ''))}</p>
      </div>
      {!readOnly && (
      <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end" onClick={(e) => e.stopPropagation()}>
        <button data-testid={`card-actions-toggle-${t.id}`} title="Ενέργειες"
          onClick={() => setActionsOpenId(actionsOpenId === `u-${t.id}` ? null : `u-${t.id}`)}
          className="sm:hidden w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:text-slate-700">
          <MoreHorizontal className="w-4 h-4" />
        </button>
        <div className={`${actionsOpenId === `u-${t.id}` ? 'flex' : 'hidden'} sm:flex items-center gap-1 flex-wrap justify-end`}>
        {t.status !== 'done' && (
          <Select
            value={String(t.duration_minutes || 30)}
            onValueChange={async (val) => {
              const v = parseInt(val, 10);
              try {
                await axios.put(`${API}/day-tasks/${t.id}`, { duration_minutes: v });
                loadPlan(selectedDate);
                offerRedistribute('duration');
              } catch (err) { toast.error('Σφάλμα αλλαγής διάρκειας'); }
            }}
          >
            <SelectTrigger
              data-testid={`task-duration-select-${t.id}`}
              title="Αλλαγή διάρκειας — θα προταθεί ανακατανομή"
              className="h-7 w-auto gap-1 px-2 border-stone-300 rounded-md bg-white text-xs focus:ring-0"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DURATIONS.map((d) => <SelectItem key={d.value} value={String(d.value)} className="text-xs">{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <button data-testid={`task-done-btn-${t.id}`} onClick={() => toggleDone(t)} title="Ολοκληρώθηκε"
          className={`cp-pop-on-hover w-7 h-7 rounded flex items-center justify-center border ${t.status === 'done' ? 'bg-green-600 border-green-600 text-white' : 'bg-white border-stone-300 text-slate-500 hover:text-green-600'}`}>
          <Check className="w-4 h-4" />
        </button>
        <button data-testid={`task-bill-btn-${t.id}`} onClick={() => openBilling(t)} title={activeTimer?.taskId === t.id ? 'Χρονόμετρο σε εξέλιξη' : (t.billed ? 'Έχει χρεωθεί — νέα χρέωση' : 'Χρέωση')}
          className={`w-7 h-7 rounded flex items-center justify-center border ${t.billed ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-stone-300 text-slate-500 hover:text-emerald-600'}${activeTimer?.taskId === t.id ? ' ring-2 ring-emerald-400 animate-pulse text-emerald-600' : ''}`}>
          <Euro className="w-4 h-4" />
        </button>
        <button data-testid={`task-reschedule-btn-${t.id}`} onClick={() => { setRescheduleEntryItem(null); setRescheduleId(t.id); setRescheduleDate(''); }} title="Μεταφορά"
          className="w-7 h-7 rounded flex items-center justify-center border bg-white border-stone-300 text-slate-500 hover:text-red-700">
          <CalendarDays className="w-4 h-4" />
        </button>
        <button data-testid={`task-delete-btn-${t.id}`} onClick={() => deleteTask(t)} title="Διαγραφή"
          className="w-7 h-7 rounded flex items-center justify-center border bg-white border-stone-300 text-slate-500 hover:text-red-600">
          <Trash2 className="w-4 h-4" />
        </button>
        </div>
      </div>
      )}
    </div>
  );

  const untimedChip = (label, Icon, colorCls, testId, items, renderItem) => items.length > 0 && (
    <div data-testid={testId} className="bg-white border border-stone-200 rounded-xl p-3">
      <p className={`text-sm font-semibold flex items-center gap-1.5 mb-2 ${colorCls}`}>
        <Icon className="w-4 h-4" />{label}
      </p>
      <div className="space-y-1.5">{items.map(renderItem)}</div>
    </div>
  );

  const dateNav = (
    <div className="flex items-center gap-1.5 flex-wrap">
      <button data-testid="day-prev-btn" onClick={() => shiftDay(-1)} title="Προηγούμενη ημέρα"
        className="flex items-center justify-center w-9 h-9 rounded-lg border border-stone-300 bg-white text-slate-600 hover:bg-stone-50">
        <ChevronLeft className="w-4 h-4" />
      </button>
      <DateField
        testId="day-date-input"
        value={selectedDate}
        onChange={(v) => setSelectedDate(v)}
        aria-label="Επιλογή ημερομηνίας"
      />
      <button data-testid="day-next-btn" onClick={() => shiftDay(1)} title="Επόμενη ημέρα"
        className="flex items-center justify-center w-9 h-9 rounded-lg border border-stone-300 bg-white text-slate-600 hover:bg-stone-50">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );

  const handleEmbeddedCapture = (e) => {
    if (e.target.closest('[data-embedded-header="true"]')) return;
    e.preventDefault();
    e.stopPropagation();
    navigate(`/day-planner?date=${selectedDate}`);
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-[#f7f2e7]'} data-testid="day-planner-page">
      {!embedded && (
      <header className="bg-white border-b border-stone-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center gap-2 sm:gap-3 flex-wrap">
          <button data-testid="day-planner-back-btn" onClick={() => navigate('/dashboard')} title="Επιστροφή"
            className="flex items-center justify-center w-9 h-9 rounded-lg border border-stone-300 bg-white text-slate-600 hover:bg-stone-50 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-base sm:text-xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-red-700" />
            Πρόγραμμα Ημέρας
          </h1>
          {selectedDate !== todayYMD && (
            <button data-testid="day-today-btn" onClick={() => setSelectedDate(todayYMD)}
              className="ml-auto h-9 px-3 rounded-lg border border-red-700 bg-red-700 text-white text-sm font-medium hover:bg-red-800">
              Σήμερα
            </button>
          )}
          <div className="max-sm:w-full max-sm:flex max-sm:justify-center max-sm:ml-0 sm:ml-auto" style={selectedDate !== todayYMD ? { marginLeft: 0 } : undefined}>{dateNav}</div>
        </div>
      </header>
      )}

      <main
        className={embedded ? `space-y-3 ${readOnly ? 'cursor-pointer' : ''}` : 'max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-4'}
        onClickCapture={readOnly ? handleEmbeddedCapture : undefined}
      >
        {embedded && (
          <div data-embedded-header="true" className="cursor-auto">
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                data-testid="embedded-planner-title-btn"
                onClick={() => navigate(`/day-planner?date=${selectedDate}`)}
                title="Άνοιγμα σελίδας Προγράμματος ημέρας"
                className="text-base sm:text-lg font-semibold text-slate-900 flex items-center gap-2 hover:text-red-700 transition-colors"
              >
                <CalendarClock className="hidden sm:block w-5 h-5 text-slate-500" />
                Πρόγραμμα ημέρας
              </button>
              {selectedDate !== todayYMD && (
                <button data-testid="embedded-today-btn" onClick={() => setSelectedDate(todayYMD)}
                  className="h-8 px-3 rounded-lg border border-red-700 bg-red-700 text-white text-sm font-medium hover:bg-red-800">
                  Σήμερα
                </button>
              )}
            </div>
            <div className="mt-2 flex justify-center">{dateNav}</div>
          </div>
        )}
        <div className={`flex items-center gap-2 flex-wrap ${embedded ? 'justify-center' : ''}`}>
          <p data-testid="day-planner-date-label" className={`text-sm sm:text-base font-semibold text-slate-800 capitalize ${embedded ? '' : 'flex-1 min-w-0 max-sm:hidden'}`}>
            {formatGreekDate(selectedDate)}{selectedDate === todayYMD ? ' · Σήμερα' : ''}
          </p>
          {!embedded && (
          <div className="flex items-center gap-1.5 max-sm:w-full max-sm:justify-center">
            <button data-testid="day-view-btn" onClick={() => setViewMode('day')}
              className={`h-9 px-3 rounded-lg border text-sm transition-colors ${viewMode === 'day' ? 'bg-purple-900 border-purple-900 text-white font-semibold' : 'bg-white border-purple-700 text-slate-600 hover:bg-purple-50'}`}>
              Ημέρα
            </button>
            <button data-testid="week-view-btn" onClick={() => setViewMode('week')}
              className={`h-9 px-3 rounded-lg border text-sm transition-colors ${viewMode === 'week' ? 'bg-purple-900 border-purple-900 text-white font-semibold' : 'bg-white border-purple-700 text-slate-600 hover:bg-purple-50'}`}>
              Εβδομάδα
            </button>
          </div>
          )}
          {!embedded && (
          <div className="flex-1 flex items-center gap-2 flex-wrap justify-end max-sm:w-full max-sm:flex-none max-sm:justify-center">
            {office && (office.my_role === 'head' || office.my_role === 'secretary') && office.members.filter((m) => m.active).length > 1 && (
              <Select value={viewMember} onValueChange={switchMember}>
                <SelectTrigger data-testid="member-switcher" className="h-9 w-auto gap-1 px-3 border-stone-300 rounded-lg bg-white text-sm focus:ring-0">
                  <User className="w-4 h-4 text-slate-500" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {office.members.filter((m) => m.active).map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.id === office.my_id ? 'Το πρόγραμμά μου' : m.name || m.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <button
              data-testid="ai-organize-btn"
              onClick={openAIModal}
              className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-purple-900 text-white text-sm font-semibold hover:bg-purple-950 shadow-sm transition-all"
            >
              <Sparkles className="w-4 h-4 text-purple-300" />
              AI Organiser
            </button>
            <button
              data-testid="day-add-task-btn"
              onClick={() => setShowAdd((v) => !v)}
              className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-red-700 text-white text-sm font-medium hover:bg-red-800 shadow-md shadow-red-900/20 transition-all"
            >
              {showAdd ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {showAdd ? 'Κλείσιμο' : 'Νέα εργασία'}
            </button>
          </div>
          )}
        </div>

        {/* Φόρμα νέας εργασίας */}
        {showAdd && (
          <div data-testid="day-add-task-form" className="bg-white border border-stone-200 rounded-2xl shadow-sm p-4 space-y-3">
            <div>
              <Label>Πελάτης (προαιρετικό)</Label>
              {!newClientMode ? (
                <div className="flex items-center gap-2 mt-1">
                  <Select value={newTask.client_id || 'none'} onValueChange={(v) => setNewTask({ ...newTask, client_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="new-task-client-select" className="flex-1 h-10 border-stone-300 rounded-lg bg-white text-sm focus:ring-0">
                      <SelectValue placeholder="Επιλέξτε πελάτη" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" className="text-sm">— Χωρίς πελάτη —</SelectItem>
                      {[...clients].sort((a, b) => clientLabel(a).localeCompare(clientLabel(b), 'el')).map((c) => (
                        <SelectItem key={c.id} value={c.id} className="text-sm">{clientLabel(c)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button type="button" data-testid="new-task-new-client-btn"
                    onClick={() => { setNewClientMode(true); setNewTask({ ...newTask, client_id: '' }); }}
                    className="h-10 px-3 rounded-lg border border-stone-300 bg-white text-sm text-slate-700 hover:border-red-700/50 whitespace-nowrap transition-colors">
                    + Νέος πελάτης
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-1">
                  <Input data-testid="new-task-client-surname-input" value={newClient.surname}
                    onChange={(e) => setNewClient({ ...newClient, surname: e.target.value })}
                    placeholder="Επώνυμο *" className="h-10 bg-white border-stone-300 rounded-lg" />
                  <Input data-testid="new-task-client-name-input" value={newClient.name}
                    onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                    placeholder="Όνομα" className="h-10 bg-white border-stone-300 rounded-lg" />
                  <button type="button" data-testid="new-task-cancel-new-client-btn"
                    onClick={() => { setNewClientMode(false); setNewClient({ surname: '', name: '' }); }}
                    className="h-10 px-2.5 rounded-lg border border-stone-300 bg-white text-slate-500 hover:text-slate-800 transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="new-task-title">Τι θα κάνετε; *</Label>
              <Input id="new-task-title" data-testid="new-task-title-input" value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="π.χ. Σύνταξη προτάσεων, τηλεφώνημα σε πελάτη..."
                className="h-10 bg-white border-stone-300 rounded-lg mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="new-task-time">Ώρα (προαιρετικό)</Label>
                <Input id="new-task-time" data-testid="new-task-time-input" type="time" value={newTask.start_time}
                  onChange={(e) => setNewTask({ ...newTask, start_time: e.target.value })}
                  className="h-10 bg-white border-stone-300 rounded-lg mt-1" />
              </div>
              <div>
                <Label htmlFor="new-task-duration">Διάρκεια</Label>
                <Select value={String(newTask.duration_minutes)} onValueChange={(v) => setNewTask({ ...newTask, duration_minutes: v })}>
                  <SelectTrigger data-testid="new-task-duration-select" className="w-full h-10 mt-1 border-stone-300 rounded-lg bg-white text-sm focus:ring-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATIONS.map((d) => <SelectItem key={d.value} value={String(d.value)} className="text-sm">{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="new-task-notes">Σημειώσεις</Label>
              <Input id="new-task-notes" data-testid="new-task-notes-input" value={newTask.notes}
                onChange={(e) => setNewTask({ ...newTask, notes: e.target.value })}
                placeholder="Προαιρετικές λεπτομέρειες..."
                className="h-10 bg-white border-stone-300 rounded-lg mt-1" />
            </div>
            <button data-testid="new-task-submit-btn" onClick={addTask}
              className="w-full h-10 rounded-lg bg-red-700 text-white text-sm font-semibold hover:bg-red-800 transition-colors">
              Προσθήκη στο πρόγραμμα
            </button>
          </div>
        )}

        {/* Εβδομαδιαία προβολή */}
        {viewMode === 'week' && !embedded && (
          <div data-testid="week-view" className="space-y-3">
            <div className="flex items-center justify-between">
              <button data-testid="week-prev-btn" onClick={() => shiftDay(-7)}
                className="flex items-center justify-center w-9 h-9 rounded-lg border border-stone-300 bg-white text-slate-600 hover:bg-stone-50">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <p className="text-sm font-semibold text-slate-800">
                {new Date(`${weekDays[0]}T12:00:00`).toLocaleDateString('el-GR', { day: 'numeric', month: 'short' })}
                {' – '}
                {new Date(`${weekDays[6]}T12:00:00`).toLocaleDateString('el-GR', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
              <button data-testid="week-next-btn" onClick={() => shiftDay(7)}
                className="flex items-center justify-center w-9 h-9 rounded-lg border border-stone-300 bg-white text-slate-600 hover:bg-stone-50">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="sm:overflow-x-auto">
              <div className="flex flex-col gap-2 sm:grid sm:grid-cols-7 sm:gap-2 sm:min-w-[980px]">
                {weekData.map(({ date, items }) => {
                  const isToday = date === todayYMD;
                  const shown = items.slice(0, 8);
                  return (
                    <div key={date} data-testid={`week-day-${date}`}
                      className={`bg-white border rounded-xl p-2 min-h-0 sm:min-h-[140px] ${isToday ? 'border-red-400 ring-1 ring-red-200' : 'border-stone-200'}`}>
                      <button
                        data-testid={`week-day-btn-${date}`}
                        onClick={() => { setSelectedDate(date); setViewMode('day'); }}
                        className={`w-full text-left mb-2 rounded-md px-1.5 py-1 hover:bg-stone-50 transition-colors flex items-baseline gap-2 sm:block ${isToday ? 'text-red-700' : 'text-slate-800'}`}
                        title="Άνοιγμα ημέρας"
                      >
                        <span className="block text-[11px] font-semibold uppercase capitalize">
                          {new Date(`${date}T12:00:00`).toLocaleDateString('el-GR', { weekday: 'short' })}
                        </span>
                        <span className="block text-lg font-bold leading-none">
                          {new Date(`${date}T12:00:00`).getDate()}
                        </span>
                      </button>
                      <div className="space-y-1">
                        {shown.map((it) => (
                          <div key={it.key} className={`border rounded-md px-1.5 py-1 ${it.cls}`}>
                            <p className="text-[11px] font-semibold" style={{ lineHeight: 1.25 }}>
                              {it.time && <span className="mr-1 opacity-80">{it.time}</span>}
                              {it.label}
                            </p>
                            {it.sub && <p className="text-[10px] opacity-75 truncate" style={{ lineHeight: 1.2 }}>{it.sub}</p>}
                          </div>
                        ))}
                        {items.length > 8 && (
                          <p className="text-[10px] text-slate-400 pl-1">+{items.length - 8} ακόμη</p>
                        )}
                        {items.length === 0 && <p className="text-[10px] text-slate-300 pl-1">—</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* 3 στήλες: Σημειώσεις (αριστερά) — Ωρολόγιο (κέντρο) — Εγγραφές ημέρας (δεξιά) */}
        {(embedded || viewMode === 'day') && (
        <div className={embedded ? 'grid grid-cols-1 gap-4 items-start' : 'grid grid-cols-1 lg:grid-cols-4 gap-4 items-start'}>
          {/* Αριστερή στήλη: Σημειώσεις ημέρας — post-it */}
          <aside data-testid="day-notes-panel" className={embedded ? 'order-2 space-y-3' : 'order-2 lg:order-1 lg:col-span-1 space-y-3'}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold flex items-center gap-1.5 text-slate-700">
                <StickyNote className="w-3.5 h-3.5 text-yellow-600" />Σημειώσεις ημέρας
              </p>
              {!readOnly && (
              <button
                data-testid="day-note-add-btn"
                onClick={() => setEditingNote({ id: null, text: '' })}
                title="Νέα σημείωση"
                className="flex items-center justify-center w-7 h-7 rounded-lg bg-yellow-400/90 text-yellow-900 hover:bg-yellow-400 shadow-sm transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
              )}
            </div>

            {editingNote && !editingNote.id && (
              <div data-testid="day-note-new" className="bg-yellow-100 border border-yellow-200/80 rounded-sm shadow-md p-3 -rotate-1">
                <textarea
                  autoFocus
                  data-testid="day-note-textarea"
                  value={editingNote.text}
                  onChange={(e) => setEditingNote({ ...editingNote, text: e.target.value })}
                  onBlur={saveEditingNote}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEditingNote(); } if (e.key === 'Escape') setEditingNote(null); }}
                  placeholder="Γράψτε τη σημείωση... (Enter)"
                  rows={3}
                  className="w-full text-sm text-slate-800 bg-transparent focus:outline-none resize-none placeholder-yellow-700/40"
                />
              </div>
            )}

            {dayNotes.map((n, idx) => (
              <div
                key={n.id}
                data-testid={`day-note-${n.id}`}
                className={`relative bg-yellow-100 border border-yellow-200/80 rounded-sm shadow-md p-3 pr-8 transition-transform hover:scale-[1.02] hover:z-10 ${idx % 2 === 0 ? 'rotate-1' : '-rotate-1'}`}
              >
                <button
                  data-testid={`day-note-delete-btn-${n.id}`}
                  onClick={() => deleteNote(n.id)}
                  title="Διαγραφή"
                  className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded text-yellow-700/50 hover:text-red-600 hover:bg-yellow-200/60"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                {editingNote && editingNote.id === n.id ? (
                  <textarea
                    autoFocus
                    data-testid={`day-note-edit-textarea-${n.id}`}
                    value={editingNote.text}
                    onChange={(e) => setEditingNote({ ...editingNote, text: e.target.value })}
                    onBlur={saveEditingNote}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEditingNote(); } if (e.key === 'Escape') setEditingNote(null); }}
                    rows={Math.max(2, Math.ceil(editingNote.text.length / 30))}
                    className="w-full text-sm text-slate-800 bg-transparent focus:outline-none resize-none"
                  />
                ) : (
                  <p
                    onClick={() => setEditingNote({ id: n.id, text: n.text })}
                    className="text-sm text-slate-800 whitespace-pre-wrap break-words cursor-text"
                    style={{ lineHeight: 1.4 }}
                    title="Κλικ για επεξεργασία"
                  >
                    {n.text}
                  </p>
                )}
              </div>
            ))}

            {dayNotes.length === 0 && !editingNote && (
              <p data-testid="day-notes-empty" className="text-xs text-slate-400 text-center py-3 border border-dashed border-stone-200 rounded-xl">
                Καμία σημείωση — πατήστε + για νέο post-it
              </p>
            )}
          </aside>

          {/* Κεντρική στήλη: ωρολόγιο + εργασίες χωρίς ώρα */}
          <section className={embedded ? 'order-3 space-y-3' : 'order-3 lg:order-2 lg:col-span-2 space-y-3'}>
            {/* Πρόγραμμα με ώρα — μόνο ό,τι έχει προγραμματισμένη ώρα */}
            <div data-testid="day-timeline" className="bg-white border border-stone-200 rounded-2xl shadow-sm p-3 sm:p-4">
              <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-3">
                <Clock className="w-4 h-4 text-slate-500" />Πρόγραμμα ημέρας
              </p>
              {loading ? (
                <p className="text-sm text-slate-500 text-center py-8">Φόρτωση...</p>
              ) : timedAgenda.length === 0 ? (
                <EmptyState
                  testId="day-agenda-empty"
                  icon={Clock}
                  compact
                  text="Καμία εργασία ή ραντεβού με προγραμματισμένη ώρα"
                  actionLabel={readOnly ? undefined : '+ Νέα εργασία'}
                  onAction={readOnly ? undefined : () => setShowAdd(true)}
                />
              ) : (
                <div className="space-y-1.5" data-testid="day-agenda-list">
                  {timedAgenda.map(({ item, kind }) => renderBlock(item, kind))}
                </div>
              )}
            </div>

            {untimedChip('Εργασίες ημέρας', Clock, 'text-slate-800', 'day-untimed-tasks', untimedTasks, untimedTaskRow)}
          </section>

          {/* Δεξιά στήλη: Εγγραφές ημέρας */}
          <aside data-testid="day-entries-panel" className={embedded ? 'order-1 space-y-3' : 'order-1 lg:order-3 lg:col-span-1 space-y-3'}>
            {untimedChip(`Λήγουν ${new Date(`${selectedDate}T12:00:00`).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' })}`, AlertTriangle, 'text-red-800', 'day-deadlines', classified.deadlines, (d) => entryCard(d, 'day-deadline'))}
            {untimedChip('Προγραμματισμένα ημέρας', CalendarDays, 'text-amber-800', 'day-scheduled', scheduledItems, (s) => entryCard(s, 'day-scheduled'))}
            {/* Εκκρεμείς εργασίες προηγούμενων ημερών — μόνο στη σημερινή ημέρα */}
            {!readOnly && selectedDate === todayYMD && overdue.length > 0 && (
              <div data-testid="overdue-tasks-banner" className="bg-amber-50 border border-amber-300 rounded-xl p-3">
                <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5 mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  Εκκρεμείς εργασίες προηγούμενων ημερών ({overdue.length})
                </p>
                <div className="space-y-1.5">
                  {overdue.map((t) => (
                    <div key={t.id} data-testid={`overdue-task-${t.id}`} className="bg-white border border-amber-200 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
                      <p className="text-xs sm:text-sm text-slate-800 min-w-0 flex-1" style={{ lineHeight: 1.25 }}>
                        <span className="text-slate-400 mr-1.5">{new Date(`${t.date}T12:00:00`).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                        {capitalizeFirst(t.title)}
                      </p>
                      <div className="flex items-center gap-1 shrink-0">
                        <button data-testid={`overdue-move-here-btn-${t.id}`} onClick={() => moveOverdueHere(t)} title="Μεταφορά εδώ"
                          className="w-7 h-7 rounded-md flex items-center justify-center border bg-white border-stone-300 text-slate-400 hover:text-red-700">
                          <CalendarDays className="w-3.5 h-3.5" />
                        </button>
                        <button data-testid={`overdue-done-btn-${t.id}`} onClick={() => toggleDone(t)} title="Ολοκληρώθηκε"
                          className="w-7 h-7 rounded-md flex items-center justify-center border bg-white border-stone-300 text-slate-400 hover:text-green-600">
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {classified.deadlines.length === 0 && scheduledItems.length === 0 && (selectedDate !== todayYMD || overdue.length === 0) && null}
          </aside>
        </div>
        )}
      </main>

      {/* Modal AI Οργάνωσης εβδομάδας */}
      {aiModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => { if (aiModal !== 'loading' && aiModal !== 'distributing' && !aiSaving) setAiModal(null); }}>
          <div data-testid="ai-plan-modal" ref={aiModalRef} className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <p className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              AI Organiser
            </p>

            {aiMode !== 'redistribute' && ['setup', 'priority', 'preview'].includes(aiModal) && (
              <div data-testid="ai-step-indicator" className="flex items-center gap-1.5">
                {[
                  { key: 'setup', n: 1, label: 'Διαθεσιμότητα' },
                  { key: 'priority', n: 2, label: 'Σειρά & διάρκειες' },
                  { key: 'preview', n: 3, label: 'Πλάνο' },
                ].map((s) => {
                  const activeIdx = { setup: 1, priority: 2, preview: 3 }[aiModal];
                  const state = s.n === activeIdx ? 'active' : s.n < activeIdx ? 'done' : 'todo';
                  return (
                    <div key={s.key} className="flex-1 min-w-0">
                      <div className={`h-1.5 rounded-full ${state === 'active' ? 'bg-red-700' : state === 'done' ? 'bg-red-300' : 'bg-slate-200'}`} />
                      <p className={`text-[10px] mt-1 truncate ${state === 'active' ? 'text-red-700 font-bold' : 'text-slate-400 font-medium'}`}>
                        {s.n}. {s.label}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}

            {aiModal === 'setup' && (
              <>
                <p className="text-sm text-slate-600">
                  {aiMode === 'redistribute'
                    ? 'Ανακατανομή: οι εκκρεμείς εργασίες (χωρίς ώρα) των επιλεγμένων ημερών θα ξανακατανεμηθούν στον διαθέσιμο χρόνο σας, με τις τρέχουσες διάρκειές τους.'
                    : 'Βήμα 1 από 3 — Πότε θα είστε στο γραφείο τις επόμενες 10 εργάσιμες ημέρες; Το AI θα φτιάξει πίνακα εργασιών κατά προτεραιότητα (με ανάλυση σε σύνταξη/κατάθεση/επίδοση όπου χρειάζεται), εσείς θα ορίσετε σειρά και διάρκειες, και μετά θα κατανεμηθούν στις ημέρες σας.'}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="ai-hours-start">Στο γραφείο από</Label>
                    <Input id="ai-hours-start" data-testid="ai-hours-start-input" type="time" value={aiHours.start}
                      onChange={(e) => updateAiHours({ ...aiHours, start: e.target.value })}
                      className="h-10 bg-white border-stone-300 rounded-lg mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="ai-hours-end">έως</Label>
                    <Input id="ai-hours-end" data-testid="ai-hours-end-input" type="time" value={aiHours.end}
                      onChange={(e) => updateAiHours({ ...aiHours, end: e.target.value })}
                      className="h-10 bg-white border-stone-300 rounded-lg mt-1" />
                  </div>
                </div>
                <div>
                  <Label>Εργάσιμες ημέρες (επόμενες 10)</Label>
                  <div className="mt-1.5 space-y-1.5">
                    {aiDays.map((d, i) => (
                      <label key={d.date} data-testid={`ai-day-row-${d.date}`}
                        className={`flex items-center gap-2.5 px-3 py-2 border rounded-lg cursor-pointer transition-colors ${d.checked ? 'bg-stone-50 border-stone-400' : 'bg-white border-stone-200 text-slate-400'}`}>
                        <input
                          type="checkbox"
                          data-testid={`ai-day-checkbox-${d.date}`}
                          checked={d.checked}
                          onChange={(e) => {
                            const next = [...aiDays];
                            next[i] = { ...d, checked: e.target.checked };
                            setAiDays(next);
                          }}
                          className="w-4 h-4 accent-red-700"
                        />
                        <span className="text-sm font-medium capitalize">{aiDayLabel(d.date)}</span>
                        {d.date === todayYMD && <span className="text-[11px] text-red-700 font-semibold">Σήμερα</span>}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="space-y-2 pt-1">
                  {aiMode === 'redistribute' ? (
                    <div className="flex gap-2">
                      <button data-testid="ai-generate-btn" onClick={runRedistribute}
                        className="flex-1 h-10 rounded-lg bg-purple-900 text-white text-sm font-semibold hover:bg-purple-950 flex items-center justify-center gap-1.5">
                        <Sparkles className="w-4 h-4 text-amber-400" />
                        Ανακατανομή εργασιών
                      </button>
                      <button data-testid="ai-close-btn" onClick={() => setAiModal(null)}
                        className="h-10 px-4 rounded-lg border border-stone-300 text-slate-600 text-sm font-medium hover:bg-stone-50">
                        Άκυρο
                      </button>
                    </div>
                  ) : (
                    <>
                      <button data-testid="ai-quick-btn" onClick={runQuickAI}
                        className="w-full h-11 rounded-lg bg-red-700 text-white text-sm font-semibold hover:bg-red-800 flex items-center justify-center gap-1.5 shadow-md shadow-red-900/20">
                        <Zap className="w-4 h-4 text-amber-300" />
                        Γρήγορη οργάνωση (1 κλικ)
                      </button>
                      <p className="text-[11px] text-slate-500 text-center">
                        Το AI ορίζει σειρά και διάρκειες και σας δείχνει κατευθείαν το τελικό πλάνο για έγκριση.
                      </p>
                      <div className="flex gap-2">
                        <button data-testid="ai-generate-btn" onClick={runAI}
                          className="flex-1 h-10 rounded-lg bg-purple-900 text-white text-sm font-semibold hover:bg-purple-950 flex items-center justify-center gap-1.5">
                          <Sparkles className="w-4 h-4 text-amber-400" />
                          Αναλυτικά (3 βήματα)
                        </button>
                        <button data-testid="ai-close-btn" onClick={() => setAiModal(null)}
                          className="h-10 px-4 rounded-lg border border-stone-300 text-slate-600 text-sm font-medium hover:bg-stone-50">
                          Άκυρο
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            {(aiModal === 'loading' || aiModal === 'distributing') && (
              <div data-testid="ai-loading" className="py-10 text-center space-y-3">
                <Loader2 className="w-8 h-8 text-slate-700 animate-spin mx-auto" />
                <p className="text-sm font-medium text-slate-800">
                  {aiModal === 'loading' ? 'Το AI αναλύει τις εκκρεμότητες…' : 'Το AI κατανέμει τις εργασίες στις ημέρες σας…'}
                </p>
                <p className="text-xs text-slate-500">
                  {aiModal === 'loading'
                    ? 'Δημιουργεί πίνακα εργασιών κατά προτεραιότητα με εκτιμώμενη διάρκεια'
                    : 'Σέβεται τη σειρά και τις διάρκειες που ορίσατε, χωρίς υπέρβαση του διαθέσιμου χρόνου'}
                </p>
              </div>
            )}

            {aiModal === 'priority' && (
              <>
                <p className="text-sm text-slate-600">
                  Βήμα 2 από 3 — <span className="font-semibold">Σειρά προτεραιότητας</span>. Σύρετε τις εργασίες για να αλλάξετε τη σειρά και ορίστε τη διάρκεια που εκτιμάτε εσείς. Μετά, το AI θα τις κατανείμει στις ημέρες σας.
                </p>
                <p className="text-xs text-slate-500" data-testid="ai-priority-totals">
                  Σύνολο εργασιών: {fmtDur(aiPriority.reduce((s, t) => s + t.duration_minutes, 0))} · Διαθέσιμος χρόνος περιόδου: {fmtDur(aiDaysInfo.reduce((s, d) => s + (d.available_minutes || 0), 0))}
                </p>
                <div className="space-y-1.5" data-testid="ai-priority-list" onDragOver={(e) => { e.preventDefault(); autoScrollOnDrag(e); }}>
                  {aiPriority.map((t, i) => (
                    <div
                      key={t._idx}
                      data-testid={`ai-priority-item-${i}`}
                      draggable
                      onDragStart={() => setAiDragFrom(i)}
                      onDragOver={(e) => { e.preventDefault(); autoScrollOnDrag(e); }}
                      onDrop={() => { if (aiDragFrom !== null) movePriority(aiDragFrom, i); setAiDragFrom(null); }}
                      className="flex items-start gap-2 border border-stone-200 bg-white rounded-lg px-2 py-2 cursor-grab active:cursor-grabbing hover:border-stone-300"
                    >
                      <span className="flex flex-col items-center shrink-0 pt-0.5">
                        <GripVertical className="w-4 h-4 text-slate-300" />
                        <span className="text-[10px] font-bold text-slate-400">{i + 1}</span>
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900" style={{ lineHeight: 1.3 }}>{capitalizeFirst(t.title)}</p>
                        {t.case_info && (
                          <p data-testid={`ai-priority-case-${i}`} className="text-[11px] text-slate-800 bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5 mt-1 inline-block" style={{ lineHeight: 1.3 }}>
                            {t.case_info}
                          </p>
                        )}
                        {(t.due || t.reason) && (
                          <p className="text-xs text-slate-500 mt-0.5" style={{ lineHeight: 1.3 }}>
                            {t.due ? `Έως ${new Date(`${t.due}T12:00:00`).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' })}` : ''}
                            {t.due && t.reason ? ' · ' : ''}{t.reason}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Select
                          value={String(t.duration_minutes)}
                          onValueChange={(val) => {
                            const v = parseInt(val, 10);
                            setAiPriority(aiPriority.map((x, xi) => (xi === i ? { ...x, duration_minutes: v } : x)));
                          }}
                        >
                          <SelectTrigger
                            data-testid={`ai-priority-duration-${i}`}
                            title="Διάρκεια που εκτιμάτε εσείς"
                            className="h-7 w-auto gap-1 px-2 border-stone-300 rounded-md bg-white text-xs focus:ring-0"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DURATIONS.map((d) => <SelectItem key={d.value} value={String(d.value)} className="text-xs">{d.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <button data-testid={`ai-priority-remove-${i}`} title="Διαγραφή — δεν θα προταθεί ξανά, παραμένει στον φάκελο υπόθεσης"
                          onClick={() => {
                            axios.post(`${API}/organiser/exclusions`, { title: t.title, case_id: t.case_id || '' }).catch(() => {});
                            setAiPriority(aiPriority.filter((_, xi) => xi !== i));
                          }}
                          className="w-6 h-6 flex items-center justify-center text-slate-300 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 pt-1">
                  <button data-testid="ai-distribute-btn" onClick={runDistribute}
                    className="flex-1 h-10 rounded-lg bg-purple-900 text-white text-sm font-semibold hover:bg-purple-950 flex items-center justify-center gap-1.5">
                    <Sparkles className="w-4 h-4 text-amber-400" />
                    Κατανομή στις ημέρες
                  </button>
                  <button data-testid="ai-priority-back-btn" onClick={() => setAiModal('setup')}
                    className="h-10 px-4 rounded-lg border border-stone-300 text-slate-600 text-sm font-medium hover:bg-stone-50">
                    Πίσω
                  </button>
                </div>
              </>
            )}

            {aiModal === 'preview' && (
              <>
                <p className="text-sm text-slate-600">
                  Βήμα 3 από 3 — Πρώτα οι σταθερές υποχρεώσεις κάθε ημέρας (δεν μετακινούνται), μετά οι εργασίες με τη σειρά και τις διάρκειες που ορίσατε. Θα μπουν στις «Εργασίες ημέρας» κάθε ημέρας.
                </p>
                <label className="flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="ai-toggle-all-checkbox"
                    checked={aiProposal.every((p) => p._checked)}
                    onChange={(e) => setAiProposal(aiProposal.map((p) => ({ ...p, _checked: e.target.checked })))}
                    className="w-4 h-4 accent-red-700"
                  />
                  Επιλογή όλων
                </label>
                <div className="space-y-3">
                  {aiDaysInfo.filter((di) => di.fixed.length > 0 || aiProposal.some((p) => p.date === di.date)).map((di) => {
                    const dayItems = aiProposal.filter((p) => p.date === di.date);
                    const planned = dayItems.filter((p) => p._checked).reduce((s, p) => s + p.duration_minutes, 0);
                    return (
                      <div key={di.date} data-testid={`ai-preview-day-${di.date}`}>
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <p className="text-xs font-bold text-slate-700 uppercase capitalize">{aiDayLabel(di.date)}</p>
                          <span data-testid={`ai-day-capacity-${di.date}`}
                            className={`text-[11px] font-semibold shrink-0 ${planned > di.available_minutes ? 'text-red-600' : 'text-slate-500'}`}>
                            {fmtDur(planned)} / {fmtDur(di.available_minutes)} διαθέσιμα
                          </span>
                        </div>
                        <div className="space-y-1.5">
                          {di.fixed.map((f, fi) => (
                            <div key={fi} data-testid={`ai-fixed-item-${di.date}-${fi}`}
                              className="flex items-center gap-2.5 border border-stone-200 bg-stone-100 rounded-lg px-2.5 py-1.5">
                              <Lock className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                              <p className="text-xs font-semibold text-slate-700 min-w-0 truncate" style={{ lineHeight: 1.3 }}>
                                <span className="text-slate-500 mr-1.5">{f.start_time}–{f.end_time}</span>{f.label}
                              </p>
                            </div>
                          ))}
                          {dayItems.map((p) => (
                            <div key={p._idx} data-testid={`ai-proposal-item-${p._idx}`}
                              className={`flex items-start gap-2.5 border rounded-lg px-2.5 py-2 ${p._checked ? 'bg-amber-50 border-amber-300' : 'bg-white border-stone-200 opacity-60'}`}>
                              <input
                                type="checkbox"
                                data-testid={`ai-proposal-check-${p._idx}`}
                                checked={p._checked}
                                onChange={(e) => setAiProposal(aiProposal.map((x) => x._idx === p._idx ? { ...x, _checked: e.target.checked } : x))}
                                className="w-4 h-4 accent-red-700 mt-0.5 shrink-0"
                              />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-slate-900" style={{ lineHeight: 1.3 }}>{capitalizeFirst(p.title)}</p>
                                {p.case_info && (
                                  <p data-testid={`ai-proposal-case-${p._idx}`} className="text-[11px] text-slate-800 bg-white border border-stone-200 rounded px-1.5 py-0.5 mt-1 inline-block" style={{ lineHeight: 1.3 }}>
                                    {p.case_info}
                                  </p>
                                )}
                                {p.reason && <p className="text-xs text-slate-500 mt-0.5" style={{ lineHeight: 1.3 }}>{p.reason}</p>}
                              </div>
                              <div className="flex flex-col items-end gap-0.5 shrink-0">
                                <span className="text-[10px] text-slate-400 flex items-center gap-0.5"><Clock className="w-3 h-3" />Διάρκεια</span>
                                <Select
                                  value={String(p.duration_minutes)}
                                  onValueChange={(val) => setAiProposal(aiProposal.map((x) => x._idx === p._idx ? { ...x, duration_minutes: parseInt(val, 10) } : x))}
                                >
                                  <SelectTrigger
                                    data-testid={`ai-proposal-duration-${p._idx}`}
                                    title="Ορίστε την πιθανή διάρκεια της απασχόλησης"
                                    className="h-7 w-auto gap-1 px-2 border-stone-300 rounded-md bg-white text-xs focus:ring-0"
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {DURATIONS.map((d) => <SelectItem key={d.value} value={String(d.value)} className="text-xs">{d.label}</SelectItem>)}
                                    {!DURATIONS.some((d) => d.value === p.duration_minutes) && (
                                      <SelectItem value={String(p.duration_minutes)} className="text-xs">{Math.round(p.duration_minutes / 6) / 10} ώρες</SelectItem>
                                    )}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          ))}
                          {dayItems.length === 0 && (
                            <p className="text-xs text-slate-400 pl-1">Καμία νέα εργασία — καλυμμένη ημέρα</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2 pt-1">
                  <button data-testid="ai-accept-btn" onClick={acceptAI} disabled={aiSaving}
                    className="flex-1 h-10 rounded-lg bg-red-700 text-white text-sm font-semibold hover:bg-red-800 disabled:opacity-60 flex items-center justify-center gap-1.5">
                    {aiSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    Προσθήκη επιλεγμένων ({aiProposal.filter((p) => p._checked).length})
                  </button>
                  <button data-testid="ai-back-btn" onClick={() => setAiModal('priority')} disabled={aiSaving}
                    className="h-10 px-4 rounded-lg border border-stone-300 text-slate-600 text-sm font-medium hover:bg-stone-50">
                    Πίσω
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal «Πόσο διήρκησε;» — καταγραφή πραγματικού χρόνου για τις εκτιμήσεις του AI */}
      {doneAsk && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDoneAsk(null)}>
          <div data-testid="task-duration-ask-modal" className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <p className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-500" />
              Πόσο διήρκησε η εργασία;
            </p>
            <p className="text-sm text-slate-700 font-medium" style={{ lineHeight: 1.35 }}>{capitalizeFirst(doneAsk.task.title)}</p>
            <p className="text-[11px] text-slate-400" style={{ lineHeight: 1.35 }}>
              Η απάντηση βελτιώνει τις εκτιμήσεις διάρκειας του AI με βάση τα δικά σας πραγματικά δεδομένα.
            </p>
            <Select value={String(doneAsk.minutes)} onValueChange={(val) => setDoneAsk({ ...doneAsk, minutes: parseInt(val, 10) })}>
              <SelectTrigger data-testid="task-duration-ask-select" className="h-10 w-full border-stone-300 rounded-lg bg-white text-sm focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATIONS.map((d) => <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2 pt-1">
              <button data-testid="task-duration-ask-confirm"
                onClick={async () => {
                  const { task, minutes } = doneAsk;
                  setDoneAsk(null);
                  timeRecordedIds.current.add(task.id);
                  try {
                    await axios.post(`${API}/task-time/record`, { title: task.title, actual_minutes: minutes, source: 'manual' });
                    toast.success('Ο χρόνος καταγράφηκε — το AI θα τον λαμβάνει υπόψη');
                  } catch (e) { /* σιωπηλά */ }
                }}
                className="flex-1 h-10 rounded-lg bg-red-700 text-white text-sm font-semibold hover:bg-red-800">
                Καταχώρηση
              </button>
              <button data-testid="task-duration-ask-skip" onClick={() => setDoneAsk(null)}
                className="h-10 px-4 rounded-lg border border-stone-300 text-slate-600 text-sm font-medium hover:bg-stone-50">
                Παράλειψη
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ξαναγεμίσματος ημέρας */}
      {refillOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !refillSaving && setRefillOpen(false)}>
          <div data-testid="refill-modal" className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <p className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Ξαναγέμισμα ημέρας
            </p>
            <p className="text-sm text-slate-600">
              Τελειώσατε νωρίτερα — επιλέξτε εργασίες επόμενων ημερών για να τις φέρετε στη σημερινή λίστα.
            </p>
            <div className="space-y-1.5">
              {refillTasks.map((t) => (
                <label key={t.id} data-testid={`refill-task-row-${t.id}`}
                  className={`flex items-start gap-2.5 border rounded-lg px-2.5 py-2 cursor-pointer transition-colors ${t._checked ? 'bg-amber-50 border-amber-300' : 'bg-white border-stone-200'}`}>
                  <input
                    type="checkbox"
                    data-testid={`refill-task-check-${t.id}`}
                    checked={t._checked}
                    onChange={(e) => setRefillTasks(refillTasks.map((x) => x.id === t.id ? { ...x, _checked: e.target.checked } : x))}
                    className="w-4 h-4 accent-red-700 mt-0.5 shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900" style={{ lineHeight: 1.3 }}>{capitalizeFirst(t.title)}</p>
                    <p className="text-xs text-slate-500 mt-0.5 capitalize">{aiDayLabel(t.date)} · {fmtDur(t.duration_minutes || 30)}</p>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex gap-2 pt-1">
              <button data-testid="refill-accept-btn" onClick={acceptRefill} disabled={refillSaving}
                className="flex-1 h-10 rounded-lg bg-red-700 text-white text-sm font-semibold hover:bg-red-800 disabled:opacity-60 flex items-center justify-center gap-1.5">
                {refillSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Μεταφορά στην ημέρα ({refillTasks.filter((t) => t._checked).length})
              </button>
              <button data-testid="refill-cancel-btn" onClick={() => setRefillOpen(false)} disabled={refillSaving}
                className="h-10 px-4 rounded-lg border border-stone-300 text-slate-600 text-sm font-medium hover:bg-stone-50">
                Άκυρο
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal μεταφοράς σε άλλη ημέρα */}
      {rescheduleId && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setRescheduleId(null)}>
          <div data-testid="reschedule-modal" className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-sm space-y-3" onClick={(e) => e.stopPropagation()}>
            <p className="text-sm font-semibold text-slate-900">Μεταφορά σε άλλη ημέρα</p>
            <InlineDatePicker
              id="reschedule-date-input"
              value={rescheduleDate}
              onChange={(e) => setRescheduleDate(e.target.value)}
              minDate={new Date()}
            />
            <div className="flex gap-2">
              <button data-testid="reschedule-confirm-btn" onClick={() => (rescheduleEntryItem ? rescheduleEntry(rescheduleEntryItem, rescheduleDate) : rescheduleTask(rescheduleId, rescheduleDate))}
                className="flex-1 h-9 rounded-lg bg-red-700 text-white text-sm font-medium hover:bg-red-800">
                Μεταφορά
              </button>
              <button data-testid="reschedule-cancel-btn" onClick={() => setRescheduleId(null)}
                className="flex-1 h-9 rounded-lg border border-stone-300 text-slate-600 text-sm font-medium hover:bg-stone-50">
                Ακύρωση
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal χρέωσης εργασίας */}
      {billingTask && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setBillingTask(null)}>
          <div data-testid="billing-modal" className="bg-white rounded-2xl shadow-xl p-5 w-full max-w-sm space-y-3" onClick={(e) => e.stopPropagation()}>
            <p className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
              <Euro className="w-4 h-4 text-red-700" />Χρέωση εργασίας
            </p>
            <p className="text-xs text-slate-500 truncate">{billingTask.title}</p>
            <div className="flex rounded-lg border border-stone-300 overflow-hidden">
              <button
                data-testid="billing-mode-hourly-btn"
                onClick={() => setBillingForm({ ...billingForm, mode: 'hourly' })}
                className={`flex-1 h-9 text-sm font-medium transition-colors ${billingForm.mode === 'hourly' ? 'bg-red-700 text-white' : 'bg-white text-slate-600 hover:bg-stone-50'}`}
              >
                Χρονοχρέωση
              </button>
              <button
                data-testid="billing-mode-flat-btn"
                onClick={() => setBillingForm({ ...billingForm, mode: 'flat' })}
                className={`flex-1 h-9 text-sm font-medium transition-colors ${billingForm.mode === 'flat' ? 'bg-red-700 text-white' : 'bg-white text-slate-600 hover:bg-stone-50'}`}
              >
                Κατ' αποκοπή
              </button>
            </div>
            <div>
              <Label htmlFor="billing-client">Πελάτης</Label>
              <SearchableClientSelect
                testId="billing-client-select"
                value={billingForm.client_id}
                onChange={(id) => setBillingForm({ ...billingForm, client_id: id })}
                clients={clients}
                allowNone
                noneLabel="— Χωρίς πελάτη —"
                className="mt-1 rounded-lg"
              />
            </div>
            {billingForm.mode === 'hourly' ? (
              <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="billing-minutes">Διάρκεια</Label>
                  <Select value={String(billingForm.minutes)} onValueChange={(v) => setBillingForm({ ...billingForm, minutes: v })}>
                    <SelectTrigger data-testid="billing-minutes-input" className="w-full h-10 mt-1 border-stone-300 rounded-lg bg-white text-sm focus:ring-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {!DURATIONS.some((d) => d.value === parseInt(billingForm.minutes, 10)) && (
                        <SelectItem value={String(billingForm.minutes)} className="text-sm">{billingForm.minutes}΄ (χρονόμετρο)</SelectItem>
                      )}
                      {DURATIONS.map((d) => <SelectItem key={d.value} value={String(d.value)} className="text-sm">{d.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="billing-rate">Ωριαία χρέωση (€)</Label>
                  <Input id="billing-rate" data-testid="billing-rate-input" type="number" min="0" step="0.01" value={billingForm.rate}
                    onChange={(e) => setBillingForm({ ...billingForm, rate: e.target.value })}
                    placeholder="π.χ. 80"
                    className="h-10 bg-white border-stone-300 rounded-lg mt-1" />
                </div>
              </div>
              <div className="border border-emerald-200 bg-emerald-50/60 rounded-lg p-2.5 flex items-center justify-between gap-2" data-testid="billing-timer-box">
                {activeTimer?.taskId === billingTask.id ? (
                  <>
                    <p className="text-base font-bold text-emerald-700 tabular-nums" data-testid="billing-timer-display">{fmtTimer(timerElapsedSec)}</p>
                    <button data-testid="billing-timer-stop-btn" onClick={stopBillingTimer}
                      className="h-8 px-3 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700">
                      Διακοπή — καταχώρηση χρόνου
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-slate-600">Χρονόμετρο για ακριβή χρονοχρέωση</p>
                    <button data-testid="billing-timer-start-btn" onClick={() => startBillingTimer(billingTask)}
                      className="h-8 px-3 rounded-lg border border-emerald-500 text-emerald-700 text-xs font-medium hover:bg-emerald-100 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />Έναρξη χρονομέτρου
                    </button>
                  </>
                )}
              </div>
              {activeTimer && activeTimer.taskId !== billingTask.id && (
                <p className="text-[11px] text-amber-600">Τρέχει ήδη χρονόμετρο στην εργασία «{activeTimer.title}».</p>
              )}
              </>
            ) : (
              <div>
                <Label htmlFor="billing-flat-amount">Ποσό αμοιβής (€)</Label>
                <Input id="billing-flat-amount" data-testid="billing-flat-amount-input" type="number" min="0" step="0.01" value={billingForm.flat_amount}
                  onChange={(e) => setBillingForm({ ...billingForm, flat_amount: e.target.value })}
                  placeholder="π.χ. 300"
                  className="h-10 bg-white border-stone-300 rounded-lg mt-1" />
              </div>
            )}
            <div>
              <Label htmlFor="billing-description">Περιγραφή</Label>
              <Input id="billing-description" data-testid="billing-description-input" value={billingForm.description}
                onChange={(e) => setBillingForm({ ...billingForm, description: e.target.value })}
                className="h-10 bg-white border-stone-300 rounded-lg mt-1" />
            </div>
            <p data-testid="billing-amount-preview" className="text-sm font-semibold text-red-700 text-right">
              Ποσό: {billingForm.mode === 'flat'
                ? (parseFloat(billingForm.flat_amount) || 0).toFixed(2)
                : (((parseFloat(billingForm.rate) || 0) * (parseInt(billingForm.minutes, 10) || 0)) / 60).toFixed(2)} €
            </p>
            <div className="flex gap-2">
              <button data-testid="billing-confirm-btn" onClick={submitBilling}
                className="flex-1 h-9 rounded-lg bg-red-700 text-white text-sm font-medium hover:bg-red-800">
                Καταχώρηση χρέωσης
              </button>
              <button data-testid="billing-cancel-btn" onClick={() => setBillingTask(null)}
                className="flex-1 h-9 rounded-lg border border-stone-300 text-slate-600 text-sm font-medium hover:bg-stone-50">
                Ακύρωση
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Καρτέλα εγγραφής — ανοίγει επιτόπου */}
      {postponeEntry && (
        <PostponementWizard
          isOpen={!!postponeEntry}
          onClose={() => setPostponeEntry(null)}
          entry={postponeEntry}
          entries={entries}
          onRefresh={refreshEntries}
        />
      )}

      {!embedded && <MyrtoChat frameMax={1280} framePad={24} />}

      {detailEntry && (
        <EntryDetail
          entry={detailEntry}
          client={clients.find((c) => c.id === detailEntry.client_id)}
          caseData={cases.find((c) => c.id === detailEntry.case_id)}
          relatedEntries={detailEntry.case_id ? entries.filter((e) => e.case_id === detailEntry.case_id) : [detailEntry]}
          onClose={() => setDetailEntry(null)}
          onRefresh={refreshEntries}
        />
      )}

      {/* Modal Χρονοχρέωσης — παραμένει διαθέσιμο για το ενεργό χρονόμετρο (localStorage) */}
    </div>
  );
}
